import { useState, useEffect, useRef } from 'react';
import { supabase } from '@/integrations/supabase/client';

interface CartItem {
  id: string;
  title: string;
  price: number;
  quantity?: number;
}

interface CartData {
  items: CartItem[];
  totalAmount: number;
  numberOfPeople: number;
  colaboradorRef?: string;
}

interface TrackingData {
  sessionId: string;
  referralCode?: string;
  utmCampaign?: string;
  utmSource?: string;
  utmMedium?: string;
  landingPage: string;
  userAgent: string;
  deviceType: string;
  browser: string;
}

export const useEnhancedCartTracking = () => {
  const [sessionId] = useState(() => 
    `session_${Date.now()}_${Math.random().toString(36).substring(7)}`
  );
  const [trackingData, setTrackingData] = useState<TrackingData>({
    sessionId,
    landingPage: window.location.pathname + window.location.search,
    userAgent: navigator.userAgent,
    deviceType: /Mobi|Android/i.test(navigator.userAgent) ? 'mobile' : 'desktop',
    browser: getBrowserName()
  });
  
  const abandonmentTimeoutRef = useRef<ReturnType<typeof setTimeout>>();
  const lastUpdateRef = useRef<Date>();

  // Initialize tracking data from URL parameters
  useEffect(() => {
    const urlParams = new URLSearchParams(window.location.search);
    setTrackingData(prev => ({
      ...prev,
      referralCode: urlParams.get('ref') || undefined,
      utmCampaign: urlParams.get('utm_campaign') || undefined,
      utmSource: urlParams.get('utm_source') || undefined,
      utmMedium: urlParams.get('utm_medium') || undefined,
    }));

    // Track initial visit
    trackReferralAnalytics('visit', 0);
  }, []);

  function getBrowserName(): string {
    const ua = navigator.userAgent;
    if (ua.includes('Chrome')) return 'Chrome';
    if (ua.includes('Firefox')) return 'Firefox';
    if (ua.includes('Safari')) return 'Safari';
    if (ua.includes('Edge')) return 'Edge';
    return 'Other';
  }

  const trackReferralAnalytics = async (step: string, value: number = 0) => {
    try {
      await supabase.functions.invoke('track-referral-analytics', {
        body: {
          ...trackingData,
          conversionStep: step,
          conversionValue: value
        }
      });
    } catch (error) {
      console.error('Error tracking referral analytics:', error);
    }
  };

  const trackCartEvent = async (
    eventType: string, 
    cartData: CartData, 
    itemData?: CartItem
  ) => {
    try {
      // Track in cart analytics
      await supabase.functions.invoke('track-cart-analytics', {
        body: {
          sessionId: trackingData.sessionId,
          eventType,
          itemId: itemData?.id,
          itemName: itemData?.title,
          itemPrice: itemData?.price,
          quantity: itemData?.quantity || 1,
          totalCartValue: cartData.totalAmount,
          referralCode: trackingData.referralCode
        }
      });

      // Track conversion step
      if (eventType === 'add_to_cart') {
        trackReferralAnalytics('add_to_cart', cartData.totalAmount);
      } else if (eventType === 'checkout_start') {
        trackReferralAnalytics('checkout_start', cartData.totalAmount);
      }

      // Google Analytics tracking
      if (window.gtag) {
        switch (eventType) {
          case 'add_to_cart':
            window.gtag('event', 'add_to_cart', {
              event_category: 'relocation_pack',
              currency: 'CHF',
              value: cartData.totalAmount / 100,
              items: cartData.items.map(item => ({
                item_id: item.id,
                item_name: item.title,
                item_category: 'relocation_service',
                quantity: item.quantity || 1,
                price: item.price / 100
              }))
            });
            break;
          case 'remove_from_cart':
            window.gtag('event', 'remove_from_cart', {
              event_category: 'relocation_pack',
              currency: 'CHF',
              value: itemData?.price ? itemData.price / 100 : 0,
              items: itemData ? [{
                item_id: itemData.id,
                item_name: itemData.title,
                item_category: 'relocation_service',
                quantity: itemData.quantity || 1,
                price: itemData.price / 100
              }] : []
            });
            break;
        }
      }
    } catch (error) {
      console.error('Error tracking cart event:', error);
    }
  };

  const trackCartUpdate = async (cartData: CartData, email?: string) => {
    if (cartData.items.length === 0) return;

    try {
      // Clear previous abandonment timeout
      if (abandonmentTimeoutRef.current) {
        clearTimeout(abandonmentTimeoutRef.current);
      }

      lastUpdateRef.current = new Date();

      // Track cart view event
      await trackCartEvent('view_cart', cartData);

      // Set new abandonment timeout (5 minutes)
      abandonmentTimeoutRef.current = setTimeout(async () => {
        console.log('Cart abandoned - tracking event');
        
        await supabase.functions.invoke('track-cart-abandonment', {
          body: {
            sessionId: trackingData.sessionId,
            email,
            selectedItems: cartData.items,
            totalAmount: cartData.totalAmount,
            numberOfPeople: cartData.numberOfPeople,
            colaboradorRef: cartData.colaboradorRef,
            referralCode: trackingData.referralCode,
            utmSource: trackingData.utmSource
          }
        });

        trackReferralAnalytics('cart_abandonment', cartData.totalAmount);
      }, 5 * 60 * 1000);

    } catch (error) {
      console.error('Error tracking cart update:', error);
    }
  };

  const trackAddToCart = async (item: CartItem, cartData: CartData) => {
    await trackCartEvent('add_to_cart', cartData, item);
  };

  const trackRemoveFromCart = async (item: CartItem, cartData: CartData) => {
    await trackCartEvent('remove_from_cart', cartData, item);
  };

  const trackCheckoutStart = async (cartData: CartData, email: string) => {
    try {
      // Clear abandonment timeout
      if (abandonmentTimeoutRef.current) {
        clearTimeout(abandonmentTimeoutRef.current);
      }

      await trackCartEvent('checkout_start', cartData);

      // Create enhanced payment attempt record
      const { data: userData } = await supabase.auth.getUser();
      const { data, error} = await supabase
        .from('payment_attempts')
        .insert({
          session_id: trackingData.sessionId,
          cart_items: {
            items: cartData.items.map(item => ({
              id: item.id,
              title: item.title,
              price: item.price,
              quantity: item.quantity || 1
            })),
            number_of_people: cartData.numberOfPeople,
            colaborador_ref: cartData.colaboradorRef,
            referral_source: trackingData.referralCode,
            utm_campaign: trackingData.utmCampaign,
            utm_source: trackingData.utmSource,
            utm_medium: trackingData.utmMedium
          },
          total_amount: cartData.totalAmount,
          user_id: userData?.user?.id || null,
          status: 'initiated'
        })
        .select()
        .single();

      if (error) throw error;

      trackReferralAnalytics('payment_initiated', cartData.totalAmount);

      return data.id;
    } catch (error) {
      console.error('Error tracking checkout start:', error);
      return null;
    }
  };

  const clearCartTracking = () => {
    if (abandonmentTimeoutRef.current) {
      clearTimeout(abandonmentTimeoutRef.current);
    }
  };

  // Cleanup on unmount
  useEffect(() => {
    return () => {
      clearCartTracking();
    };
  }, []);

  return {
    sessionId: trackingData.sessionId,
    trackingData,
    trackCartUpdate,
    trackAddToCart,
    trackRemoveFromCart,
    trackCheckoutStart,
    trackReferralAnalytics,
    clearCartTracking
  };
};